<script setup>
import CFButton from '@/ui/Button/CFButton.vue';
import { useTicketStore } from '#modules/support/stores/ticket.store.js';
import { useSupportStore } from '#modules/support/stores/support.store.js';
import { useSupportMessagesQuery } from '#modules/support/queries/useSupportMessagesQuery.js';
import TicketStartStep from '#modules/support/components/Ticket/Steps/TicketStartStep.vue';
import CFContentPlaceholders from '@/shared/components/CFContentPlaceholders.vue';
import CFContentPlaceholdersBox from '@/shared/components/CFContentPlaceholdersBox.vue';
import CFIntersectionTrigger from '@/shared/components/CFIntersectionTrigger.vue';
import CFCircleSpinner from '@/shared/components/CFCircleSpinner.vue';
import SupportSidebarSupportMessage from '@/modules/support/components/SupportSidebarSupportMessage.vue';
import { useRouter } from 'vue-router';
import SupportSidebarSearchAndFilters from '@/modules/support/components/SupportSidebarSearchAndFilters.vue';
import CFSidebar from '@/shared/components/CFSidebar.vue';

const ticketStore = useTicketStore();
const supportStore = useSupportStore();
const router = useRouter();

const setActiveTicket = (ticket) => {
    supportStore.setActiveTicket(ticket);
    router.push({ name: 'support conversation', params: { id: ticket.id } });
};

const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isPending } = useSupportMessagesQuery();

const getNextPage = () => {
    if (!isFetchingNextPage.value && hasNextPage.value) {
        fetchNextPage();
    }
};
</script>

<template>
    <CFSidebar>
        <div class="relative flex h-full scroll-ps-20 flex-col overflow-x-hidden overflow-y-auto">
            <div class="bg-grey-light sticky top-0 z-10 shrink-0 px-6 pt-12 lg:pt-6">
                <div class="flex flex-col gap-2">
                    <CFButton
                        size="lg"
                        type="button"
                        class="flex-1"
                        @click="ticketStore.open(TicketStartStep)"
                    >
                        {{ $t('support.new_ticket') }}
                    </CFButton>
                </div>
                <SupportSidebarSearchAndFilters />
            </div>

            <div
                v-if="!isPending"
                class="shrink-0 grow basis-0 px-6"
                :class="isFetching ? 'pb-20' : 'pb-8'"
            >
                <template
                    v-for="(group, index) in data?.pages ?? []"
                    :key="index"
                >
                    <SupportSidebarSupportMessage
                        v-for="ticket in group.data"
                        :key="ticket.id"
                        :ticket="ticket"
                        :is-active="supportStore.isActiveTicket(ticket)"
                        @click="setActiveTicket(ticket)"
                    />
                </template>

                <CFIntersectionTrigger @intersected="getNextPage" />

                <div
                    v-if="isFetching"
                    class="bg-grey-light absolute right-0 bottom-0 left-0 py-6"
                >
                    <CFCircleSpinner class="mx-auto size-6" />
                </div>
            </div>

            <CFContentPlaceholders
                v-else
                class="px-6"
            >
                <CFContentPlaceholdersBox class="my-4 h-16" />
                <hr class="border-neutrals-700" />
                <CFContentPlaceholdersBox class="my-4 h-16" />
            </CFContentPlaceholders>
        </div>
    </CFSidebar>
</template>
